import {
  CloseOutlined,
  PaperClipOutlined,
  SendOutlined,
  SmileOutlined
} from "@ant-design/icons"
import {
  Alert,
  Avatar,
  Button,
  Flex,
  Popover,
  Progress,
  Space,
  Typography
} from "antd"
import type { TextAreaRef } from "antd/es/input/TextArea"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../components/Input/TextArea.tsx"
import {
  type ComposerState,
  applyMentionToText,
  mentionQueryAt,
  survivingMentions,
  toComposerState,
  toMarkerBody
} from "../helpers/mentions.ts"
import type { Message, Participant } from "../types/messaging.types.ts"

// Enough to cover every keyboard a construction PM actually reaches for,
// without shipping a picker library for a chat composer.
const EMOJI = [
  "👍", "👎", "✅", "❌", "🎉", "🙏", "👀", "🔥",
  "😀", "😅", "😊", "😍", "🤔", "😬", "😢", "😡",
  "🏗️", "🔨", "📐", "📋", "📸", "📄", "⚠️", "⏰"
]

const formatBytes = (bytes: number): string => {
  const units = ["B", "KB", "MB", "GB"]
  let size = bytes
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`
}

/** A picked image, shown before sending. The object URL is a resource, so it
 *  is released when the chip is removed rather than left to the page's life. */
function FilePreviewThumb({ file }: { file: File }) {
  const [url, setUrl] = useState<string>("")

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])

  if (!url) return null
  return (
    <img
      src={url}
      alt={file.name}
      className={"size-10 shrink-0 rounded object-cover"}
    />
  )
}

interface MessageComposerProps {
  disabled: boolean
  /** Written by the server from the record and the other party. */
  placeholder?: string
  disabledReason?: string
  participants: Participant[]
  /** Left out of the picker: you cannot usefully mention yourself. */
  currentUserId?: string
  replyTo: Message | null
  draft: string
  onDraftChange: (draft: string) => void
  onCancelReply: () => void
  onSend: (body: string, files: File[]) => void
  /** 0–100 while an upload is in flight, null otherwise. */
  uploadProgress: number | null
}

export function MessageComposer({
  disabled,
  placeholder,
  disabledReason,
  participants,
  currentUserId,
  replyTo,
  draft,
  onDraftChange,
  onCancelReply,
  onSend,
  uploadProgress
}: MessageComposerProps) {
  const { t } = useTranslation()
  const inputRef = useRef<TextAreaRef>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [caret, setCaret] = useState(0)
  const [dragging, setDragging] = useState(false)
  const uploading = uploadProgress !== null

  // The person sees names; the server stores markers. Identity is kept against
  // the range a name occupies, so two people with the same name — or one person
  // mentioned twice — stay distinct, and deleting the name drops the mention.
  const [composer, setComposer] = useState<ComposerState>(() =>
    toComposerState(draft, participants)
  )
  const emittedRef = useRef(draft)

  // Adopt the draft when it changes from outside: cleared after a send, or
  // loaded from the server. Our own edits are already reflected.
  useEffect(() => {
    if (draft === emittedRef.current) return
    emittedRef.current = draft
    setComposer(toComposerState(draft, participants))
  }, [draft, participants])

  const updateComposer = (next: ComposerState) => {
    const alive = { text: next.text, mentions: survivingMentions(next.text, next.mentions) }
    setComposer(alive)
    const body = toMarkerBody(alive.text, alive.mentions)
    emittedRef.current = body
    onDraftChange(body)
  }

  const mentionQuery = useMemo(
    () => mentionQueryAt(composer.text, caret),
    [composer.text, caret]
  )
  const [activeSuggestion, setActiveSuggestion] = useState(0)
  // Escape closes the picker without clearing what has been typed. Keyed to
  // the term, so the next keystroke brings it back rather than stranding the
  // writer with a dead @ for the rest of the line.
  const [dismissed, setDismissed] = useState(false)
  useEffect(() => {
    setActiveSuggestion(0)
    setDismissed(false)
  }, [mentionQuery?.term])
  const pickerOpen = Boolean(mentionQuery) && !dismissed

  const suggestions = useMemo(() => {
    if (!mentionQuery) return []
    const term = mentionQuery.term.toLowerCase()
    return participants
      // The roster is kept whole so a mention of you still renders your name;
      // only the picker leaves you out.
      .filter((person) => person.id !== currentUserId)
      .filter((person) => person.full_name.toLowerCase().includes(term))
      .slice(0, 8)
  }, [mentionQuery, participants, currentUserId])

  useEffect(() => {
    if (replyTo) inputRef.current?.focus()
  }, [replyTo])

  const chooseMention = (person: Participant) => {
    if (!mentionQuery) return
    const next = applyMentionToText(composer, mentionQuery, caret, person)
    updateComposer(next.state)
    setCaret(next.caret)
    inputRef.current?.focus()
  }

  const submit = () => {
    if (disabled) return
    if (!composer.text.trim() && files.length === 0) return
    onSend(toMarkerBody(composer.text, composer.mentions), files)
    setFiles([])
  }

  const addFiles = (incoming: FileList | null) => {
    if (!incoming?.length) return
    setFiles((current) => [...current, ...Array.from(incoming)])
  }

  if (disabled) {
    return (
      <Alert
        type={"info"}
        showIcon
        className={"m-3"}
        message={
          disabledReason ??
          t("messages.composer.readOnly", "This conversation is read-only.")
        }
      />
    )
  }

  return (
    <div
      className={`shrink-0 border-t border-slate-200 bg-white p-3 ${
        dragging ? "ring-2 ring-teal-400 ring-inset" : ""
      }`}
      onDragOver={(event) => {
        event.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault()
        setDragging(false)
        addFiles(event.dataTransfer.files)
      }}
      data-testid={"message-composer"}
    >
      {/* The drop target and the top border keep the panel's full width —
          a file dragged anywhere over the composer still lands — while the
          controls inside sit in the same centred column as the messages. */}
      <div className={"mx-auto w-full max-w-[900px]"}>
        {replyTo && (
          <Flex
            align={"center"}
            justify={"space-between"}
            className={
              "mb-2 rounded-md border-l-2 border-blue-400 bg-slate-50 px-2 py-1"
            }
          >
            <div className={"min-w-0"}>
              <Typography.Text strong className={"block text-xs"}>
                {t("messages.composer.replyingTo", "Replying to {{name}}", {
                  name: replyTo.sender?.full_name ?? ""
                })}
              </Typography.Text>
              <Typography.Text type={"secondary"} ellipsis className={"text-xs"}>
                {replyTo.body}
              </Typography.Text>
            </div>
            <Button
              type={"text"}
              size={"small"}
              icon={<CloseOutlined />}
              onClick={onCancelReply}
              aria-label={t("messages.composer.cancelReply", "Cancel reply")}
            />
          </Flex>
        )}

        {files.length > 0 && (
          <Space wrap className={"mb-2"}>
            {files.map((file, index) => (
              <Flex
                key={`${file.name}-${index}`}
                align={"center"}
                gap={"small"}
                className={"rounded-md border border-slate-200 px-2 py-1"}
              >
                {/* A thumbnail of the actual file, so what is about to be sent
                    is visible before it is sent rather than only afterwards.
                    The object URL is revoked when the chip goes away. */}
                {file.type.startsWith("image/") && (
                  <FilePreviewThumb file={file} />
                )}
                <div className={"min-w-0"}>
                  <Typography.Text className={"block text-xs"} ellipsis>
                    {file.name}
                  </Typography.Text>
                  <Typography.Text type={"secondary"} className={"text-xs"}>
                    {formatBytes(file.size)}
                  </Typography.Text>
                </div>
                <Button
                  type={"text"}
                  size={"small"}
                  icon={<CloseOutlined />}
                  onClick={() =>
                    setFiles((current) => current.filter((_, i) => i !== index))
                  }
                  aria-label={t("messages.composer.removeFile", "Remove file")}
                />
              </Flex>
            ))}
          </Space>
        )}

        {uploadProgress !== null && (
          <Progress percent={uploadProgress} size={"small"} className={"mb-2"} />
        )}

        {pickerOpen && (
          <div
            data-testid={"mention-suggestions"}
            className={
              "mb-2 max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm"
            }
          >
            {suggestions.length === 0 ? (
              // Saying so is better than an empty box, and far better than the
              // old behaviour of offering the one name in the room: your own.
              <div className={"px-3 py-2 text-sm text-slate-500"}>
                {t(
                  "messages.composer.noMentionable",
                  "No other participants to mention"
                )}
              </div>
            ) : (
              suggestions.map((person, index) => (
                <button
                  key={person.id}
                  type={"button"}
                  data-testid={"mention-option"}
                  aria-selected={index === activeSuggestion}
                  className={[
                    "flex w-full items-center gap-2 px-3 py-2 text-left",
                    index === activeSuggestion ? "bg-slate-100" : "hover:bg-slate-50"
                  ].join(" ")}
                  onMouseEnter={() => setActiveSuggestion(index)}
                  onClick={() => chooseMention(person)}
                >
                  <Avatar size={24} src={person.avatar || undefined}>
                    {person.full_name?.[0]}
                  </Avatar>
                  <span className={"min-w-0 flex-1 truncate text-sm"}>
                    {person.full_name}
                  </span>
                  <span className={"shrink-0 text-xs text-slate-500"}>
                    {person.role_label}
                  </span>
                </button>
              ))
            )}
          </div>
        )}

        <Flex align={"end"} gap={"small"}>
          <TextArea
            ref={inputRef}
            // The composer holds mention markers (`@[uuid]`) and a caret this
            // component tracks to offer suggestions; auto-capitalization
            // rewrites both, so it is off here deliberately.
            disableAutoCapitalize
            value={composer.text}
            autoSize={{ minRows: 1, maxRows: 8 }}
            placeholder={
              placeholder ?? t("messages.composer.placeholder", "Write a message…")
            }
            // The same string the placeholder shows, not the generic one: the
            // server names the room in it ("Message Emma about Oak Street
            // Renovation…"), and a customer read the fixed label aloud was told
            // they were messaging the project team.
            aria-label={
              placeholder ?? t("messages.composer.placeholder", "Write a message…")
            }
            onChange={(event) => {
              // Typing may have eaten part of a name; `updateComposer` drops any
              // mention whose label no longer sits where it did, which is how a
              // mention is removed by editing.
              updateComposer({ ...composer, text: event.target.value })
              setCaret(event.target.selectionStart ?? event.target.value.length)
            }}
            onKeyDown={(event) => {
              // While the picker is open the arrows and Enter belong to it;
              // otherwise Enter sends and Shift+Enter breaks the line.
              if (pickerOpen && suggestions.length > 0) {
                if (event.key === "ArrowDown") {
                  event.preventDefault()
                  setActiveSuggestion((index) => (index + 1) % suggestions.length)
                  return
                }
                if (event.key === "ArrowUp") {
                  event.preventDefault()
                  setActiveSuggestion(
                    (index) => (index - 1 + suggestions.length) % suggestions.length
                  )
                  return
                }
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault()
                  chooseMention(suggestions[activeSuggestion] ?? suggestions[0])
                  return
                }
                if (event.key === "Escape") {
                  event.preventDefault()
                  setDismissed(true)
                  return
                }
              }
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault()
                submit()
              }
            }}
            onSelect={(event) =>
              setCaret(
                (event.target as HTMLTextAreaElement).selectionStart ?? caret
              )
            }
          />

          <input
            ref={fileInputRef}
            type={"file"}
            multiple
            hidden
            onChange={(event) => {
              addFiles(event.target.files)
              event.target.value = ""
            }}
          />

          <Flex align={"center"} className={"shrink-0 rounded-md bg-slate-50 px-1"}>
            <Button
              type={"text"}
              size={"small"}
              icon={<PaperClipOutlined />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              aria-label={t("messages.composer.attach", "Attach files")}
            />

          <Popover
            trigger={"click"}
            content={
              <div className={"grid w-56 grid-cols-8 gap-1"}>
                {EMOJI.map((emoji) => (
                  <button
                    key={emoji}
                    type={"button"}
                    className={"rounded p-1 text-lg hover:bg-slate-100"}
                    onClick={() =>
                      updateComposer({
                        ...composer,
                        text: composer.text + emoji
                      })
                    }
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            }
          >
              <Button
                type={"text"}
                size={"small"}
                icon={<SmileOutlined />}
                aria-label={t("messages.composer.emoji", "Add emoji")}
              />
            </Popover>
            <Button
              type={"text"}
              size={"small"}
              onClick={() =>
                updateComposer({ ...composer, text: `${composer.text}@` })
              }
              aria-label={t("messages.composer.mention", "Mention someone")}
            >
              @
            </Button>
          </Flex>

          <Button
            type={"primary"}
            icon={<SendOutlined />}
            onClick={submit}
            loading={uploading}
            // Nothing to send, or a send already in flight: a second click while
            // the files are still going up posts the message twice.
            disabled={uploading || (!composer.text.trim() && files.length === 0)}
          >
            {t("messages.composer.send", "Send")}
          </Button>
        </Flex>
      </div>
    </div>
  )
}
